import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';
import type ComponentReviewThreadModel from 'tio-common/models/component-review-thread';
import type ComponentReviewCommentModel from 'tio-common/models/component-review-comment';

/**
 * ComponentReviewService
 *
 * Manages the component-level review/discussion system for the reimagined UI.
 * Mirrors the DataAudit toggle pattern: toggle on → highlight sections → click to discuss.
 *
 * Uses Ember Data store backed by JSONAPI resources at /api/v2/component-review-threads.
 */
export default class ComponentReviewService extends Service {
  @service declare router: RouterService;
  @service declare store: Store;

  // ─── Toggle State ───────────────────────────────────────────────────────

  @tracked isReviewModeEnabled = this.initReviewModeState();

  @tracked isDrawerOpen = false;

  @tracked activeReviewId?: string;

  @tracked activePagePath?: string;

  // ─── Cached thread data (loaded per page) ──────────────────────────────

  @tracked threads: ComponentReviewThreadModel[] = [];
  @tracked isLoading = false;

  private routeChangeHandler?: () => void;

  // ─── Initialization ────────────────────────────────────────────────────

  private initReviewModeState(): boolean {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('reimagined-review-mode');
    const isEnabled = stored === 'true';
    if (isEnabled) {
      document.body.classList.add('reimagined-review-mode');
    }
    return isEnabled;
  }

  // ─── Current Page Path ─────────────────────────────────────────────────

  get currentPagePath(): string {
    return this.router.currentURL?.split('?')[0] ?? '/';
  }

  // ─── Toggle ────────────────────────────────────────────────────────────

  toggleReviewMode = () => {
    this.isReviewModeEnabled = !this.isReviewModeEnabled;
    if (typeof window !== 'undefined') {
      document.body.classList.toggle('reimagined-review-mode', this.isReviewModeEnabled);
      localStorage.setItem('reimagined-review-mode', String(this.isReviewModeEnabled));
    }
    // Load threads and subscribe to route changes when enabling
    if (this.isReviewModeEnabled) {
      this.loadThreadsForPage();
      this.subscribeToRouteChanges();
    } else {
      this.unsubscribeFromRouteChanges();
    }
  };

  // ─── Route Change Subscription ─────────────────────────────────────────

  private subscribeToRouteChanges() {
    if (this.routeChangeHandler) return;
    this.routeChangeHandler = () => {
      if (this.isReviewModeEnabled) {
        this.loadThreadsForPage();
      }
    };
    this.router.on('routeDidChange', this.routeChangeHandler);
  }

  private unsubscribeFromRouteChanges() {
    if (this.routeChangeHandler) {
      this.router.off('routeDidChange', this.routeChangeHandler);
      this.routeChangeHandler = undefined;
    }
  }

  willDestroy() {
    super.willDestroy();
    this.unsubscribeFromRouteChanges();
  }

  // ─── Data Loading ──────────────────────────────────────────────────────

  /**
   * Load all threads for the current page from the API.
   * Called when review mode is enabled or page changes.
   */
  loadThreadsForPage = async (pagePath?: string): Promise<void> => {
    const path = pagePath ?? this.currentPagePath;
    this.isLoading = true;
    try {
      const threads = await this.store.query('component-review-thread', {
        filter: { 'page-path': path },
        include: 'comments,comments.author,created-by',
      });
      this.threads = threads.slice() as ComponentReviewThreadModel[];
    } catch (error) {
      console.warn('[ComponentReview] Failed to load threads:', error);
      this.threads = [];
    } finally {
      this.isLoading = false;
    }
  };

  // ─── Thread Queries ────────────────────────────────────────────────────

  /**
   * Get all threads for a specific reviewId on the current page.
   * Thread scope = reviewId + pagePath.
   */
  getThreadsForComponent(reviewId: string, pagePath?: string): ComponentReviewThreadModel[] {
    const path = pagePath ?? this.currentPagePath;
    return this.threads.filter((t) => t.reviewId === reviewId && t.pagePath === path);
  }

  /**
   * Get all open threads for the current page
   */
  getOpenThreadsForPage(pagePath?: string): ComponentReviewThreadModel[] {
    const path = pagePath ?? this.currentPagePath;
    return this.threads.filter((t) => t.pagePath === path && t.isOpen);
  }

  /**
   * Count open threads for a component (used for badge)
   */
  getOpenThreadCount(reviewId: string, pagePath?: string): number {
    return this.getThreadsForComponent(reviewId, pagePath).filter((t) => t.isOpen).length;
  }

  /**
   * Count total comments across all threads for a component
   */
  getTotalCommentCount(reviewId: string, pagePath?: string): number {
    return this.getThreadsForComponent(reviewId, pagePath).reduce(
      (sum, t) => sum + (t.comments?.length ?? 0),
      0
    );
  }

  // ─── Drawer Actions ────────────────────────────────────────────────────

  openDrawer = (reviewId: string, pagePath?: string) => {
    this.activeReviewId = reviewId;
    this.activePagePath = pagePath ?? this.currentPagePath;
    this.isDrawerOpen = true;
  };

  closeDrawer = () => {
    this.isDrawerOpen = false;
    this.activeReviewId = undefined;
    this.activePagePath = undefined;
  };

  // ─── Thread Mutations ─────────────────────────────────────────────────

  /**
   * Create a new thread for a component via the API.
   * Also creates the initial comment.
   */
  createThread = async (
    reviewId: string,
    initialComment: string,
    screenshotDataUrl?: string
  ): Promise<ComponentReviewThreadModel | undefined> => {
    try {
      const thread = this.store.createRecord('component-review-thread', {
        reviewId,
        pagePath: this.currentPagePath,
        status: 'open',
        screenshotUrl: screenshotDataUrl ?? null,
      }) as ComponentReviewThreadModel;

      await thread.save();

      // Create the initial comment (author set server-side)
      const comment = this.store.createRecord('component-review-comment', {
        body: initialComment,
        thread,
      }) as ComponentReviewCommentModel;
      await comment.save();

      // Refresh local cache
      await this.loadThreadsForPage();
      return thread as ComponentReviewThreadModel;
    } catch (error) {
      console.error('[ComponentReview] Failed to create thread:', error);
      return undefined;
    }
  };

  /**
   * Add a comment to an existing thread
   */
  addComment = async (
    threadId: string,
    body: string
  ): Promise<ComponentReviewCommentModel | undefined> => {
    try {
      const thread = this.store.peekRecord(
        'component-review-thread',
        threadId
      ) as ComponentReviewThreadModel | null;
      if (!thread) return undefined;

      const comment = this.store.createRecord('component-review-comment', {
        body,
        thread,
      }) as ComponentReviewCommentModel;
      await comment.save();

      // Refresh to get updated thread with new comment
      await this.loadThreadsForPage();
      return comment as ComponentReviewCommentModel;
    } catch (error) {
      console.error('[ComponentReview] Failed to add comment:', error);
      return undefined;
    }
  };

  /**
   * Resolve a thread
   */
  resolveThread = async (threadId: string): Promise<void> => {
    try {
      const thread = this.store.peekRecord(
        'component-review-thread',
        threadId
      ) as ComponentReviewThreadModel | null;
      if (!thread) return;
      thread.status = 'resolved';
      await thread.save();
      await this.loadThreadsForPage();
    } catch (error) {
      console.error('[ComponentReview] Failed to resolve thread:', error);
    }
  };

  /**
   * Reopen a resolved thread
   */
  reopenThread = async (threadId: string): Promise<void> => {
    try {
      const thread = this.store.peekRecord(
        'component-review-thread',
        threadId
      ) as ComponentReviewThreadModel | null;
      if (!thread) return;
      thread.status = 'open';
      await thread.save();
      await this.loadThreadsForPage();
    } catch (error) {
      console.error('[ComponentReview] Failed to reopen thread:', error);
    }
  };

  // ─── Screenshot Capture ────────────────────────────────────────────────

  /**
   * Capture a screenshot of a DOM element.
   * Returns a base64 data URL or undefined if capture fails.
   * TODO: Install html2canvas and wire up ActiveStorage for real screenshot support.
   */
  async captureScreenshot(_element: HTMLElement): Promise<string | undefined> {
    // html2canvas not yet installed — return undefined for now
    return undefined;
  }
}

// ─── Ember Service Registry ───────────────────────────────────────────────

declare module '@ember/service' {
  interface Registry {
    'component-review': ComponentReviewService;
  }
}
